import { Component, OnInit, OnDestroy, ViewChild, HostListener, AfterViewInit, ViewEncapsulation, Renderer2, ElementRef, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { MatSidenav } from '@angular/material/sidenav';
import { Subscription } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { NavigationContextService } from '../../services/navigation-context.service';
import { OrganizationService } from '../../services/organization.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-sidebar-nav',
  templateUrl: './sidebar-nav.component.html',
  styleUrls: ['./sidebar-nav.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SidebarNavComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('sidenav') sidenav: MatSidenav;
  @ViewChild('sidebarContainer', { read: ElementRef }) sidebarContainer: ElementRef;
  organization;
  isSuperAdmin = false;
  isAdmin = false;
  isLessee = false;
  menuOpen = false;
  isMobile = false;
  hasPropertyContext = false;
  appVersion = environment.version || 'dev';
  private subs = new Subscription();

  constructor(
    private router: Router,
    public authService: AuthService,
    private organizationService: OrganizationService,
    private renderer: Renderer2,
    private cdr: ChangeDetectorRef,
    private navContext: NavigationContextService
  ) {}

  ngOnInit() {
    this.checkRoles();
    this.checkMobile();
    this.hasPropertyContext = this.navContext.hasPropertyContext;
    this.subs.add(
      this.navContext.propertyId$.subscribe((id) => {
        this.hasPropertyContext = !!id;
      })
    );
    this.organizationService.getOrganization().then((org) => {
      this.organization = org;
    });
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  ngAfterViewInit() {
    // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      // Ensure sidenav state is correct after view init
      if (this.sidenav && !this.isMobile) {
        this.sidenav.open();
        this.menuOpen = true;
      }
      this.updateMobileStyles();
      this.cdr.detectChanges();
    }, 0);
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.checkMobile();
  }

  checkMobile() {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth <= 960;
    
    // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      if (!this.isMobile) {
        this.menuOpen = true; // Always open on desktop
        if (this.sidenav && !this.sidenav.opened) {
          this.sidenav.open();
        }
      } else {
        // On mobile, closed by default
        if (!wasMobile) {
          // Just switched to mobile - close the menu
          this.menuOpen = false;
        }
        if (this.sidenav && this.sidenav.opened && !this.menuOpen) {
          this.sidenav.close();
        }
      }
      this.updateMobileStyles();
      this.cdr.detectChanges();
    }, 0);
  }

  updateMobileStyles() {
    if (!this.sidebarContainer) return;
    
    const container = this.sidebarContainer.nativeElement;
    const drawerContent = container.querySelector('.mat-drawer-content') as HTMLElement;
    const sidenavContent = container.querySelector('.mat-sidenav-content') as HTMLElement;
    const drawerContainer = container.querySelector('.mat-drawer-container') as HTMLElement;
    
    if (this.isMobile) {
      // Force full width on mobile using inline styles
      if (drawerContent) {
        drawerContent.style.setProperty('width', '100%', 'important');
        drawerContent.style.setProperty('margin-left', '0', 'important');
        drawerContent.style.setProperty('max-width', '100%', 'important');
        drawerContent.style.setProperty('min-width', '0', 'important');
      }
      if (sidenavContent) {
        sidenavContent.style.setProperty('width', '100%', 'important');
        sidenavContent.style.setProperty('margin-left', '0', 'important');
        sidenavContent.style.setProperty('max-width', '100%', 'important');
        sidenavContent.style.setProperty('min-width', '0', 'important');
      }
      if (drawerContainer) {
        drawerContainer.style.setProperty('width', '100%', 'important');
        drawerContainer.style.setProperty('max-width', '100%', 'important');
      }
    } else {
      // Desktop styles
      if (drawerContent) {
        drawerContent.style.setProperty('margin-left', '280px', 'important');
        drawerContent.style.setProperty('width', 'calc(100% - 280px)', 'important');
      }
      if (sidenavContent) {
        sidenavContent.style.setProperty('margin-left', '280px', 'important');
        sidenavContent.style.setProperty('width', 'calc(100% - 280px)', 'important');
      }
    }
  }

  checkRoles() {
    const auth = this.authService.getAuthentication();
    if (auth && auth.roles) {
      const roles = Array.isArray(auth.roles) ? auth.roles : [];
      this.isSuperAdmin = roles.some(function(r) {
        return r === 'SUPER_ADMIN' || r === 'SuperAdmin';
      });
      this.isAdmin = this.authService.isAdmin();
      this.isLessee = this.authService.isLessee();
    }
  }

  toggleMenu() {
    this.menuOpen = !this.menuOpen;
    if (this.sidenav) {
      if (this.menuOpen) {
        this.sidenav.open();
      } else {
        this.sidenav.close();
      }
    }
    // Update styles after menu toggle
    setTimeout(() => this.updateMobileStyles(), 100);
  }

  onSidebarClose() {
    if (this.isMobile) {
      this.menuOpen = false;
    }
  }

  navigate(path: string) {
    const clearProperty =
      path === '/admin-landing' ||
      path === '/reports' ||
      path === '/createUser' ||
      path === '/organization-settings' ||
      path === '/super-admin' ||
      path === '/lessee-landing' ||
      path === '/';
    this.navContext.navigate(path, { clearProperty });
    if (this.isMobile) {
      this.menuOpen = false; // Close menu on mobile after navigation
      if (this.sidenav && this.sidenav.opened) {
        this.sidenav.close();
      }
    }
  }

  isActive(path: string): boolean {
    const current = this.router.url.split('?')[0];

    if (path === '/admin-landing') {
      return current === '/admin-landing' || current === '/admin-landing/';
    }

    if (path === '/manage-leases') {
      return current === '/manage-leases'
        || current.startsWith('/start-lease')
        || current.startsWith('/modify-lease');
    }

    if (path === '/admin-invoices') {
      return current === '/admin-invoices' || current.startsWith('/invoice/');
    }

    if (path === '/organization-expenses') {
      return current === '/organization-expenses'
        || current.startsWith('/property-expenses')
        || current.startsWith('/expense-receipt')
        || current.startsWith('/org-expense-receipt');
    }

    if (path === '/property-statements') {
      return current === '/property-statements' || current.startsWith('/statement-view');
    }

    if (path === '/all-messages') {
      return current === '/all-messages' || current.startsWith('/message-respond');
    }

    if (path === '/createUser') {
      return current === '/createUser' || current.startsWith('/createUser/');
    }

    return current === path || current.startsWith(path + '/');
  }

  get expensesLabel(): string {
    return this.hasPropertyContext ? 'Property expenses' : 'Expenses';
  }

  logout() {
    this.navContext.clearProperty();
    this.authService.logout();
  }
}
